import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, Output, EventEmitter, Input, Injector } from '@angular/core';
import validationEngine from 'devextreme/ui/validation_engine';
import notify from 'devextreme/ui/notify';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import dxDataGrid from 'devextreme/ui/data_grid';
// Import any necessary services - adjust based on your project structure
import { TaskDataService } from '../../../shared/services/task-data.service';
import { LoggingService } from '../../../framework/logging/logging.service';
import { AppComponent } from '../../../app.component';
import { SeverityLevel } from '../../../framework/logging/severity-level.model';
import { getNotificationTimeout } from '../../../types/resource';
import {DefaultNotificationData,IDefaultNotificationData, ILoanBorrower, ITaskRequest, TaskRequest } from '../../../modules/tasks/model/types';
import { FormUtils } from '../../../shared/components/framework/services/form-builder.service';
import { LoanDataBase } from '../baseclass/loandata-base';
import { FeatureFlagsService } from '../../../shared/services/feature-flags.service';

@Component({
  standalone: false,
  selector: 'app-default-notification',
  templateUrl: './default-notification.component.html',
  styleUrls: ['./default-notification.component.scss']
})
export class DefaultNotificationComponent extends LoanDataBase implements OnInit, AfterViewInit {
  @ViewChild('formElement') formElement!: ElementRef;

  @Input() taskTemplateName: string;
  @Input() task: ITaskRequest;

  @Output() onAddNewTaskEventEmitter: EventEmitter<any> = new EventEmitter();

  notificationData: IDefaultNotificationData;
  requestTypes: any[];
  isLoading: boolean = false;
  files: File[] = [];
  today: Date = new Date();
  isEditable: boolean = false;
  isInvoiceChecked: boolean = false;

  // Make loan borrowers reactive so the grid updates when data arrives
  private loanBorrowersSubject$ = new BehaviorSubject<ILoanBorrower[]>([]);
  loanBorrowers$ = this.loanBorrowersSubject$.asObservable();

  loanBorrowersGrid: any = null;
  isGridInEditMode: boolean = false;
  formItems: any[] = [];

  constructor(
    taskDataService: TaskDataService,
    private readonly loggingService: LoggingService,
    private readonly injector: Injector,
    featureFlagsService: FeatureFlagsService
  ) {
    super(taskDataService, featureFlagsService);
    // Load loan borrowers asynchronously
    // this.taskDataService.getLoanBorrowers().subscribe({
    //   next: (loanBorrowersResponse) => {
    //     const borrowers = (loanBorrowersResponse as any)[1];
    //     const activeBorrowers = borrowers.filter((borrower: any) => borrower.status === 'Active');
    //     this.loanBorrowersSubject$.next(activeBorrowers);
    //     console.log('Loaded loan borrowers:', activeBorrowers);
    //   },
    //   error: (err) => {
    //     this.loggingService.logError(err, SeverityLevel.Error);
    //     console.error('Failed to load loan borrowers:', err);
    //   }
    // });
  }


  onFileUpload(file: File) {
    this.files.push(file);
  }

  onFileRemove(file: File)
   {
    if(file != null && file != undefined)
    {
      this.files = this.files.filter(f => f !== file);
    }
   }

  async onSubmit(e: any) {
    e.preventDefault();

    // Clean up empty rows from loan borrowers grid
    this.cleanupLoanBorrowers();

    // Validate that at least one loan-borrower is selected
    if (!this.notificationData.selectedLoanBorrowers || this.notificationData.selectedLoanBorrowers.length === 0) {
      notify('At least one Loan Number must be selected', 'error', getNotificationTimeout());
      return;
    }

    let validationResult = validationEngine.validateGroup("formGroup");

    if (validationResult.isValid) {
      try {
        this.isLoading = true;

        let request: ITaskRequest = new TaskRequest();
        let requestType = this.requestTypes.find((t) => t.name == this.notificationData.requestType);
        request.templateId = requestType.id;

        request.name = this.notificationData.requestType;
        // Use the first selected loan number as reference name
        request.referenceName = this.notificationData.selectedLoanBorrowers[0].loanNumber;

      request.referenceType = requestType.referenceType;

      // Prepare notification data for submission - map selectedLoanBorrowers to plain objects
      // excluding the 'key' property used by the grid
      const submissionData = {
        ...this.notificationData,
        selectedLoanBorrowers: this.notificationData.selectedLoanBorrowers.map(item => ({
          loanNumber: item.loanNumber,
          borrower: item.borrower,
          dealNumber: item.dealNumber
        }))
      };

      // Log the data to verify proper serialization
      console.log('Submission data:', submissionData);
      console.log('Stringified data:', JSON.stringify(submissionData));

      let formData: FormData = new FormData();
      formData.append('templateId', request.templateId);
      formData.append('name', request.name);
      formData.append('referenceName', request.referenceName);
      formData.append('requestJsonData', this.removeProxyData(JSON.stringify(submissionData)));
      formData.append('referenceType', request.referenceType);        if(this.isEditable)
          formData.append('parentId', this.task.parentId);

        this.files.forEach((f) => { formData.append('files', f, f.name) });

        // Submit task
        const response = await lastValueFrom(this.taskDataService.insertTaskAsFormData(formData));
        notify(response[0], 'information', getNotificationTimeout());



        this.isLoading = false;
        this.formElement.nativeElement.reset();
        this.formElement.nativeElement.submit();
      }

      catch (err: any) {
        this.isLoading = false;
        this.loggingService.logError(err, SeverityLevel.Error);
        notify(err.message, 'error', getNotificationTimeout());
      }
    }
  }

  // Generate unique IDs for grid rows
  private generateUniqueId(): string {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

  // Grid template for loan-borrower selection
  loanBorrowerGridTemplate = (formItemData, container) => {
    // Create grid container
    const gridElement = document.createElement("div");
    gridElement.style.marginTop = "10px";
    container.appendChild(gridElement);

    // Initialize the data source if it doesn't exist
    if (!this.notificationData.selectedLoanBorrowers) {
      this.notificationData.selectedLoanBorrowers = [];
    }

    // Flag to prevent onContentReady from adding multiple rows
    let isGridInitialized = false;

    const grid = new dxDataGrid(gridElement, {
      dataSource: this.notificationData.selectedLoanBorrowers,
      keyExpr: "key",
      columns: [
        {
          dataField: "loanNumber",
          caption: "Loan Number",
          dataType: "string",
          lookup: {
          dataSource: () => this.loanBorrowersSubject$.value,
            valueExpr: "loanNumber",
            displayExpr: "loanNumber",
          },
          cellTemplate: (container, options) => {
            const loanBorrowerData = this.loanBorrowersSubject$.value;
            const loanBorrower = loanBorrowerData.find(
              (a) => a.loanNumber === options.value,
            );
            if (loanBorrower) {
              const displayText = `${loanBorrower.loanNumber}`;
              container.innerHTML = `<span style="color: inherit; font-weight: normal; word-wrap: break-word; white-space: normal;">${displayText}</span>`;
            } else {
              // Show placeholder text when cell is empty
              container.innerHTML = `<span style="color: #999; font-style: italic;">Click to add loan number</span>`;
            }
            // Set minimum height on cell to prevent jumping
            container.style.minHeight = "36px";
            container.style.display = "flex";
            container.style.alignItems = "center";
          },
        },
        {
          dataField: "borrower",
          caption: "Borrower",
          dataType: "string",
          allowEditing: false,
          calculateCellValue: (rowData) => {
            if (rowData.loanNumber) {
              const loanBorrower = this.loanBorrowersSubject$.value.find(
                (lb) => lb.loanNumber === rowData.loanNumber
              );
              return loanBorrower ? loanBorrower.borrower : "";
            }
            return "";
          },
        },
      ],
      wordWrapEnabled: true,
      editing: {
        mode: "cell",
        allowAdding: false,
        allowUpdating: true,
        allowDeleting: true,
        confirmDelete: false,
        selectTextOnEditStart: true,
        startEditAction: "click",
      },
      onRowRemoved: (e) => {
        // Row has been deleted, ensure there's at least one empty row
        const hasEmptyRow = this.notificationData.selectedLoanBorrowers.some(
          (item) => !item.loanNumber
        );
        if (!hasEmptyRow || this.notificationData.selectedLoanBorrowers.length === 0) {
          this.addEmptyRowToLoanBorrowerGrid(grid);
        }
      },
      toolbar: {
        visible: false,
      },
      showBorders: true,
      columnAutoWidth: true,
      rowAlternationEnabled: true,
      onCellClick: (e) => {
        if (e.rowType === "data" && e.column.dataField === "loanNumber") {
          grid.editCell(e.rowIndex, e.columnIndex);
        }
      },
      onContentReady: (e) => {
        if (!isGridInitialized) {
          isGridInitialized = true;
          if (this.notificationData.selectedLoanBorrowers.length === 0) {
            this.addEmptyRowToLoanBorrowerGrid(grid);
          }
        }
      },
      onEditorPreparing: (e) => {
        if (e.dataField === "loanNumber" && e.parentType === "dataRow") {
          // Filter by dealNumber if there's already a selection
          const selectedLoansWithDealNumber = this.notificationData.selectedLoanBorrowers.filter(
            (item) => item.loanNumber && item.dealNumber
          );

          let filteredDataSource = this.loanBorrowersSubject$.value;

          if (selectedLoansWithDealNumber.length > 0) {
            // Get the dealNumber from the first selected loan
            const firstDealNumber = selectedLoansWithDealNumber[0].dealNumber;

            // Filter to only show loans with the same dealNumber
            filteredDataSource = this.loanBorrowersSubject$.value.filter(
              (lb) => lb.dealNumber === firstDealNumber
            );
          }

          // Further filter to exclude already selected loan numbers (except current row)
          const existingLoanNumbers = this.notificationData.selectedLoanBorrowers
            .map((item) => item.loanNumber)
            .filter((ln) => ln && ln !== e.row.data.loanNumber);

          e.editorOptions.dataSource = filteredDataSource.filter(
            (lb) => !existingLoanNumbers.includes(lb.loanNumber)
          );

          e.editorOptions.onValueChanged = (args) => {
            if (args.value) {
              // Find the selected loan borrower to get the dealNumber and borrower
              const selectedLoanBorrower = this.loanBorrowersSubject$.value.find(
                (lb) => lb.loanNumber === args.value
              );

              if (selectedLoanBorrower) {
                // Store the dealNumber and borrower in the row data
                e.row.data.dealNumber = selectedLoanBorrower.dealNumber;
                e.row.data.borrower = selectedLoanBorrower.borrower;
              }

              // Use setValue to properly set the loan number value in the editor
              e.setValue(args.value);
              // Close the editor to trigger save and refresh
              setTimeout(() => {
                e.component.closeEditCell();
              }, 0);
            }
          };
        }
      },
      onRowUpdated: (e) => {
        this.isGridInEditMode = false;
        if (e.data.loanNumber) {
          const hasEmptyRow = this.notificationData.selectedLoanBorrowers.some(
            (item) => !item.loanNumber
          );
          if (!hasEmptyRow) {
            this.addEmptyRowToLoanBorrowerGrid(grid);
          }
        }
        grid.refresh();
      },
      onEditingStart: (e) => {
        this.isGridInEditMode = true;
      },
      onEditCanceled: (e) => {
        this.isGridInEditMode = false;
      },
    });

    this.loanBorrowersGrid = grid;

    // Subscribe to loan borrowers updates and refresh grid when data arrives
    this.loanBorrowersSubject$.subscribe(borrowers => {
      if (borrowers && borrowers.length > 0 && grid) {
        // Repaint the grid to update the lookup datasource
        grid.repaint();
      }
    });
  }

  // Add empty row to loan-borrower grid
  private addEmptyRowToLoanBorrowerGrid(gridInstance: any): void {
    const newRow = {
      key: this.generateUniqueId(),
      loanNumber: null,
      borrower: null,
      dealNumber: null,
    };

    this.notificationData.selectedLoanBorrowers.push(newRow);
    gridInstance.refresh();
  }

  // Clean up empty rows before submission
  private cleanupLoanBorrowers(): void {
    if (this.notificationData.selectedLoanBorrowers) {
      this.notificationData.selectedLoanBorrowers = this.notificationData.selectedLoanBorrowers.filter(
        (row) => row.loanNumber && row.loanNumber.trim() !== ''
      );
    }
  }

  private buildFormItems(): void {
    this.formItems = [
      {
        dataField: "requestType",
        editorType: "dxTextBox",
        label: { text: "Request Type" },
        cssClass: "dx-widget",
        value: "Notice of EOD",
        editorOptions: {
          readOnly: true,
        },
        validationRules: [
          { type: "required", message: "Request Type is required" },
        ],
      },
      {
        itemType: "custom",
        name: "loanBorrowerGrid",
        label: { visible: false },
        template: this.loanBorrowerGridTemplate,
      },
      {
        dataField: "eodDate",
        editorType: "dxDateBox",
        label: { text: "EOD Date" },
        cssClass: "dx-widget",
        editorOptions: {
          required: true,
          displayFormat: this.featureFlagsService.getDateDisplayFormat(),
          openOnFieldClick: true,
          showClearButton: true
        },
        validationRules: [
        { type: "required", message: "EOD Date is required" },
        ],
      },
      {
        itemType: "group",
        colCount: 3,
        colSpan: 2,
        items: [
          {
            dataField: "suspendBilling",
            editorType: "dxCheckBox",
            label: { text: "Suspend Billing?" },
            colSpan: 1,
            cssClass: "dx-widget",
          },
          {
            dataField: "isUrgent",
            editorType: "dxCheckBox",
            label: { text: "Rush Job?" },
            colSpan: 1,
            cssClass: "dx-widget",
          }
        ],
      },

      {
        itemType: "simple",
        name: "fileUpload",
        label: {
          text: "Upload Files *",
        },
        template: "fileUploadTemplate",
        required: true,
        text: "Upload Files *"
      },
      {
          // work around to bind file upload component to form and validate it
          dataField: "fileUploadProxy",
          colSpan: 2,
          itemType: "simple",
          label: { visible: false },
          editorType: "dxTextBox",
          editorOptions: {
            visible: false,
            value: this.files.length.toString(),
          },
          validationRules: [
            {
              type: "custom",
              message: "At least one file is required",
              validationCallback: (e) => this.onValidateFileAttachments(e),
              reevaluate: true,
            },
          ],
        },
      {
        cssClass: "borrow-submit",
        editorType: "dxButton",
        editorOptions: {
          text: "Submit",
          useSubmitBehavior: true
        },
      },
    ];
  }

  onValidateFileAttachments(e: any): boolean {
    if (this.files.length == 0) {
      e.isValid = false;
      return false;
    }
    e.isValid = true;
    return true;
  }

  override async ngOnInit() {

        try{
          this.isLoading = true;
          const requestData = await lastValueFrom(this.taskDataService.getTaskTemplatesByType(this.taskTemplateName));
          this.requestTypes = requestData[1];

          await super.ngOnInit();

          // Load loan borrowers asynchronously
          this.taskDataService.getLoanBorrowers().subscribe({
            next: (loanBorrowersResponse) => {
              const borrowers = (loanBorrowersResponse as any)[1];
              // const activeBorrowers = borrowers.filter((borrower: any) => borrower.status === 'Active');
              // console.log('Active borrowers:', activeBorrowers);
              this.loanBorrowersSubject$.next(borrowers);
            },
            error: (err) => {
              this.loggingService.logError(err, SeverityLevel.Error);
              console.error('Failed to load loan borrowers:', err);
            }
          });

          this.isEditable = this.task != null ? true : false;

          if(this.isEditable)
          {
            this.notificationData = FormUtils.mapFromTask<DefaultNotificationData>(this.task, DefaultNotificationData);
            // Initialize isInvoiceChecked based on loaded data
            this.isInvoiceChecked = this.notificationData.invoice || false;
            // console.log(this.notificationData);
          }
          else{
             this.notificationData = new DefaultNotificationData();
             this.isInvoiceChecked = false;
          }

          // Build form items immediately without waiting for loan borrowers
          this.buildFormItems();
      // Use setTimeout to ensure the form is fully rendered before setting selected index
      setTimeout(() => {
        if (this.requestTypes && this.requestTypes.length > 0) {
          this.notificationData.requestType = this.requestTypes[0].name;
        }
      }, 100);

          this.isLoading = false;
        }
        catch(err: any)
        {
          this.loggingService.logError(err, SeverityLevel.Error);
          this.isLoading = false;
          notify(err.message, 'error', getNotificationTimeout());
        }
  }

  ngAfterViewInit(): void {
    // Check if this page content needs horizontal scrolling after view is initialized
    setTimeout(() => {
      try {
        const appComponent = this.injector.get(AppComponent);
        appComponent.updateScrollbarVisibility();
        // console.log('Checked page content scrolling for notifications component');
      } catch (error) {
        console.warn('Could not access AppComponent for scrolling check:', error);
      }
    }, 100); // Small delay to ensure DOM is fully rendered
  }

  onInvoiceCheckboxChanged(e: any): void {
    this.isInvoiceChecked = e.value;
    // Rebuild form items to update drawFee field state and validation
    this.buildFormItems();
  }

  removeProxyData(data: string): string {
    if(data) {
      let json = JSON.parse(data);
      delete json["fileUploadProxy"];
      return JSON.stringify(json);
    }
    return "{}";
  }

}
